import {Image} from '../Image';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSetSelector} from '../../hooks/set-selector';
import {CameraBrandModel} from '../../types/control';
import {Configs} from '../../configs';
import {Video} from "../Video";

import {Utf8ArrayToStr} from "../../utils/array";
import {VideoIframe} from "../VideoIframe";
import {WindowUtil} from "../../utils/window";
import {useHistory} from "react-router-dom";

type Props = {
    id: string;
    quality: 360 | 720;
    className?: string;
    mode?: 'auto' | 'snapshot' | 'rtsp' | 'video_cgi' | 'mjpeg' | 'video' | 'video_iframe';
    brand?: CameraBrandModel;
}

type RendererProps = {
    ip: string;
    port?: number;
} & Props;

export function VideoCGIRenderer(props: RendererProps) {
    const subUrl = useMemo(() => {
        const urls: { [key: number]: string } = {};

        if (props.brand.video_cgi.stream.q_720p) {
            urls[720] = props.brand.video_cgi.stream.q_720p;
        }

        if (props.brand.video_cgi.stream.q_360p) {
            urls[360] = props.brand.video_cgi.stream.q_360p;
        }

        if (props.quality === 720) {
            return urls[720] || urls[360];
        }

        return urls[360] || urls[720];
    }, [props.brand, props.quality]);

    const url = 'http://' + props.ip + ':' + props.port;

    return (
        <Image
            className={props.className}
            disablePrefix={true}
            src={url + subUrl}
            alt=""/>
    );
}

export function VideoRenderer(props: RendererProps) {
    const [src, setSrc] = useState(null);
    let websocket = new WebSocket('ws://teknopark.inovathings.com:8083/ws/live?suuid=H264_AAC');
    websocket.binaryType = 'arraybuffer';
    let mediaSource = new MediaSource();
    let buffer;
    let queue = [];
    let streamingStarted = false;
    const onOpen = useCallback(() => {
        console.log('Camera Connected');
    }, []);

    useEffect(() => {
        websocket.onopen = onOpen;
    }, [onOpen]);

    const onMessage = useCallback((event) => {
        if (typeof event.data !== 'string') {
            console.log('event', event)
            const data = new Uint8Array(event.data);
            if (data[0] == 9) {
                const decoded_arr = data.slice(1);
                let mimeCodec;
                if (window.TextDecoder) {
                    mimeCodec = new TextDecoder("utf-8").decode(decoded_arr);
                } else {
                    mimeCodec = Utf8ArrayToStr(decoded_arr);
                }
                buffer = mediaSource.addSourceBuffer('video/mp4; codecs="' + mimeCodec + '"');
                buffer.mode = "segments";
                buffer.addEventListener('update', function () {
                    if (queue.length > 0 && !buffer.updating) {
                        buffer.appendBuffer(queue.shift());
                    }
                });
            }
            if (buffer.updating) {
                queue.push(event.data);
            } else {
                buffer.appendBuffer(event.data);
            }
        }
    }, []);

    useEffect(() => {
        websocket.onmessage = onMessage;
        // eslint-disable-next-line
    }, [onMessage]);
    //
    useEffect(() => {
        startup();
    }, []);

    const startup = () => {
        const src = window.URL.createObjectURL(mediaSource);
        setSrc(src);
        mediaSource.addEventListener('sourceopen', (e) => {
        }, false);
    }
    return (
        <Video
            className={props.className}
            disablePrefix={true}
            src={src}/>
    );
}


export function VideoIframeRenderer(props: RendererProps) {
    const url = 'http://' + props.ip + ':' + props.port;

    return (
        <VideoIframe
            className={props.className}
            disablePrefix={true}
            src={url}
            alt=""/>
    );
}

export function MJPEGRenderer(props: RendererProps) {
    const quality = useMemo(() => {
        const urls: { [key: number]: number } = {};

        if (props.brand.mjpeg.stream.q_720p) {
            urls[720] = 720;
        }

        if (props.brand.mjpeg.stream.q_360p) {
            urls[360] = 360;
        }

        if (props.quality === 720) {
            return urls[720] || urls[360];
        }

        return urls[360] || urls[720];
    }, [props.brand, props.quality]);
    const url = `/camera/mjpeg/${props.id}/${quality}`;

    return (
        <Image
            className={props.className}
            src={url}
            alt=""
            interval={props.brand.mjpeg.delay_ms}
        />
    );
}

export function RTSPRenderer(props: RendererProps) {
    const url = `/camera/rtsp/${props.id}`;
    console.log(props)
    return (
        <Image
            className={props.className}
            src={url}
            alt=""
        />
    );
}

export function SnapshotRenderer(props: RendererProps) {
    const random = useMemo(() => Math.random(), []);
    const url = `/camera/snapshot/${props.id}?v=${random}`;
    return (
        <Image
            className={props.className}
            src={url}
            alt=""
        />
    );
}

export function CameraRenderer(props: Props) {
    const cameraSet = useSetSelector(state => state.control.cameraList);
    const cameraBrandSet = useSetSelector(state => state.control.cameraBrandList);
    const selectedCamera = useMemo(() => cameraSet[props.id], [cameraSet, props.id]);
    const selectedCameraBrand = useMemo(() => selectedCamera && cameraBrandSet[selectedCamera.camera_brand_id], [cameraBrandSet, selectedCamera]);
    const history = useHistory();
    useEffect(() => {
        setTimeout(() => {
            WindowUtil.stop()
            history.push('/');
        }, 5 * 60000);
    }, []);
    if (!selectedCamera || !selectedCameraBrand) {
        return null;
    }

    if (props.mode === 'snapshot' && (selectedCamera.rtsp || selectedCamera.mjpeg)) {
        return (
            <SnapshotRenderer
                {...props}
                brand={selectedCameraBrand}
                ip={selectedCamera.ip}
            />
        );
    }

    if (selectedCamera.video_cgi && (props.mode === 'video_cgi' || props.mode === 'auto' || props.mode === 'snapshot')) {
        return (
            <VideoCGIRenderer
                {...props}
                brand={selectedCameraBrand}
                ip={selectedCamera.ip}
                port={selectedCamera.video_cgi.port}
            />
        );
    }

    if (selectedCamera.video && props.mode === 'video') {
        return (
            <VideoRenderer
                {...props}
                brand={selectedCameraBrand}
                ip={selectedCamera.ip}
                port={selectedCamera.video_cgi.port}
            />
        );
    }

    if (selectedCamera.video_iframe && props.mode === 'video_iframe') {
        return (
            <VideoIframeRenderer
                {...props}
                brand={selectedCameraBrand}
                ip={selectedCamera.ip}
                port={selectedCamera.video_iframe.port}
            />
        );
    }

    if (selectedCamera.mjpeg && (props.mode === 'mjpeg' || props.mode === 'auto')) {
        return (
            <MJPEGRenderer
                {...props}
                brand={selectedCameraBrand}
                ip={selectedCamera.ip}
                port={selectedCamera.mjpeg.port}
            />
        );
    }

    if (selectedCamera.rtsp && (props.mode === 'rtsp' || props.mode === 'auto')) {
        return (
            <RTSPRenderer
                {...props}
                brand={selectedCameraBrand}
                ip={selectedCamera.ip}
                port={selectedCamera.rtsp.port}
            />
        );
    }

    return null;
}
